import { ok } from "neverthrow";
import { match, P } from "ts-pattern";

import { dayjs, mustBeDefined } from "@thalamos/common";

import type { IncidentWorkItemWithContext } from "../../types/incidentWorkItem.js";
import type {
  RecordPolicePresenceMinimumFields,
  RequestPolicePresenceMinimumFields,
} from "../../types/workflow/index.js";
import {
  combinePlacesOfSafetyAndOfficerPresenceForms,
  type CombinePlacesOfSafetyAndOfficerPresenceFormsResult,
} from "../combinePlacesOfSafetyAndOfficerPresenceForms.js";

export const getLatestPolicePresenceUpdate = ({
  latestPlaceOfSafety,
}: {
  latestPlaceOfSafety: CombinePlacesOfSafetyAndOfficerPresenceFormsResult;
}) => {
  // Get the latest Place of Safety. As the officer is most likely there,
  // we only want to calculate against events there.
  const updates = latestPlaceOfSafety.policePresence
    .map((policePresence) => {
      const main = policePresence.sections.requestPolicePresenceMain;
      const record = policePresence.sections.recordPolicePresence;

      // Only consider if has a createdAt
      if (!main.createdAt?.value) return undefined;

      const createdAt = dayjs.utc(main.createdAt.value);
      const hasRemainedAtPlaceOfSafety =
        record?.hasRemainedAtPlaceOfSafety?.value ?? undefined;
      const source = record?.source;

      return { createdAt, hasRemainedAtPlaceOfSafety, source };
    })
    .filter(
      (
        x,
      ): x is {
        createdAt: dayjs.Dayjs;
        hasRemainedAtPlaceOfSafety: RecordPolicePresenceMinimumFields["hasRemainedAtPlaceOfSafety"]["value"];
        source: RequestPolicePresenceMinimumFields["source"];
      } =>
        x !== undefined &&
        x.hasRemainedAtPlaceOfSafety !== undefined &&
        x.source?.value.toLowerCase() !== "emha",
    )
    .sort((a, b) => b.createdAt.unix() - a.createdAt.unix());

  return updates.at(0);
};

const describeDuration = ({
  asOf,
  from,
}: {
  asOf: dayjs.Dayjs;
  from: dayjs.Dayjs;
}) => {
  const gapInHours = asOf.diff(from, "hours");
  const gapInMinutes = asOf.subtract(gapInHours, "hours").diff(from, "minutes");

  const gapInHoursString =
    gapInHours === 0
      ? undefined
      : `${gapInHours.toFixed()} hour${gapInHours === 1 ? "" : "s"}`;
  const gapInMinutesString =
    gapInMinutes === 0
      ? undefined
      : `${gapInMinutes.toFixed()} minute${gapInMinutes === 1 ? "" : "s"}`;

  const description =
    gapInHours === 0 && gapInMinutes === 0
      ? "just now"
      : [gapInHoursString, gapInMinutesString].filter(Boolean).join(" ").trim();

  return { description, gapInHours, gapInMinutes };
};

/**
 * Calculates from the current state whether we are due an update for police presence.
 * @returns {boolean} True if an update is due, false otherwise.
 */
export const isPolicePresenceUpdateDue = ({
  incidentWorkItem,
  updateIntervalMinutes,
  asOf,
}: {
  incidentWorkItem: IncidentWorkItemWithContext;
  updateIntervalMinutes?: number;
  asOf: dayjs.Dayjs;
}):
  | { status: "not-applicable" }
  | { status: "not-updated"; description: string }
  | { status: "not-due"; description: string; latestPlaceOfSafetyName?: string }
  | {
      status: "due";
      description: string;
      latestPlaceOfSafetyName?: string;
      latestPlaceOfSafetyArrivalDateTime?: dayjs.Dayjs;
    } => {
  // If there is no interval set, or it is zero, we never need an update.
  if (updateIntervalMinutes === undefined || updateIntervalMinutes <= 0) {
    return { status: "not-applicable" };
  }

  // If the incident is ended, we don't need updates.
  if (incidentWorkItem.incidentWorkItem.status === "ended") {
    return { status: "not-applicable" };
  }

  // Get the latest Place of Safety. As the officer is most likely there,
  // we only want to calculate against events there.
  const latestPlaceOfSafety = combinePlacesOfSafetyAndOfficerPresenceForms(
    incidentWorkItem,
  )
    .unwrapOr([])
    .at(0);

  if (latestPlaceOfSafety === undefined) {
    return {
      status: "not-updated",
      description: "Not updated",
    };
  }

  const latestPlaceOfSafetyName =
    latestPlaceOfSafety.placeOfSafety.sections.recordPlaceOfSafetyMain
      .placeOfSafety?.name;

  const latestPlaceOfSafetyArrivalDateTime = ok(
    latestPlaceOfSafety.placeOfSafety.sections.recordPlaceOfSafetyArrival
      ?.arrivalDateTime?.value,
  )
    .andThen(mustBeDefined())
    .map((value) => dayjs(value))
    .unwrapOr(undefined);

  // Find the timestamp of the latest requestPolicePresence...
  const lastUpdateTimestampPolicePresence = getLatestPolicePresenceUpdate({
    latestPlaceOfSafety,
  });
  if (lastUpdateTimestampPolicePresence !== undefined) {
    const lastUpdateTimestamp = lastUpdateTimestampPolicePresence.createdAt;

    // Describe this gap in human readable text
    const description = ok(
      describeDuration({
        asOf,
        from: lastUpdateTimestamp,
      }),
    )
      .map(({ description, gapInHours, gapInMinutes }) =>
        gapInHours === 0 && gapInMinutes === 0
          ? `Last updated ${description}`
          : `Last updated ${description} ago`,
      )
      .unwrapOr("Unknown");

    const nextUpdateDueBy = dayjs(lastUpdateTimestamp).add(
      updateIntervalMinutes,
      "minutes",
    );

    return {
      status: nextUpdateDueBy.isBefore(asOf) ? "due" : "not-due",
      description,
      latestPlaceOfSafetyName,
      latestPlaceOfSafetyArrivalDateTime,
    };
  }

  // Find the timestamp for the latest PlaceOfSafetyArrival
  const lastUpdateTimestampArrivePoS = (() => {
    const value =
      latestPlaceOfSafety.placeOfSafety.sections.recordPlaceOfSafetyArrival
        ?.arrivalDateTime?.value;

    return value ? dayjs(value) : undefined;
  })();

  if (lastUpdateTimestampArrivePoS !== undefined) {
    const lastUpdateTimestamp = lastUpdateTimestampArrivePoS;

    const nextUpdateDueBy = dayjs(lastUpdateTimestamp).add(
      updateIntervalMinutes,
      "minutes",
    );

    return nextUpdateDueBy.isBefore(asOf)
      ? {
          status: "due",
          description: "Not updated",
          latestPlaceOfSafetyName,
          latestPlaceOfSafetyArrivalDateTime,
        }
      : {
          status: "not-updated",
          description: "Not updated",
        };
  }

  // We didn't find any timestamp, we are not updated.
  return {
    status: "not-updated",
    description: "Not updated",
  };
};

/**
 * Calculates from the current state whether we are due an email to the owner to update for police presence.
 * @returns {boolean} True if an update is due, false otherwise.
 */
export const isPolicePresenceEmailUpdateDue = ({
  incidentWorkItem,
  ownerLastEmailedAt,
  updateIntervalMinutes,
  asOf,
}: {
  incidentWorkItem: IncidentWorkItemWithContext;
  updateIntervalMinutes?: number;
  ownerLastEmailedAt?: dayjs.Dayjs;
  asOf: dayjs.Dayjs;
}) => {
  // If there is no interval set, or it is zero, we never need an update.
  if (updateIntervalMinutes === undefined || updateIntervalMinutes <= 0) {
    return { shouldEmailOfficer: false };
  }

  const updateDue = isPolicePresenceUpdateDue({
    incidentWorkItem,
    updateIntervalMinutes,
    asOf,
  });

  const officerEmailedRecently =
    ownerLastEmailedAt !== undefined &&
    ownerLastEmailedAt.add(updateIntervalMinutes, "minutes").isAfter(asOf);

  const result: {
    shouldEmailOfficer: boolean;
    durationDescription?: string;
    latestPlaceOfSafetyName?: string;
  } = match([updateDue, officerEmailedRecently])
    .with([P._, true], () => ({
      shouldEmailOfficer: false,
    }))
    .with(
      [{ status: P.union("not-applicable", "not-updated", "not-due") }, P._],
      () => ({
        shouldEmailOfficer: false,
      }),
    )
    .with([{ status: "due" }, false], ([updateDue]) => {
      const { description } = describeDuration({
        asOf,
        from:
          updateDue.latestPlaceOfSafetyArrivalDateTime ??
          dayjs(incidentWorkItem.incidentWorkItem.createdAt),
      });

      return {
        shouldEmailOfficer: true,
        durationDescription: description,
        latestPlaceOfSafetyName: updateDue.latestPlaceOfSafetyName,
      };
    })
    .exhaustive();

  return result;
};
